import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePathname, useRouter } from '@/compat/navigation';
import { useAuthState } from '@/hooks/useAuthState';
import { fetchDynamicMenu, selectAllowedRoutes, selectMenuStatus } from '@/store/feature/layoutConfigSlice';
import { logout } from '@/store/feature/auth/authSlice';
import type { AppDispatch } from '@/store/store';
import { routeAllowed } from '../model/routeAccess';

export function useMenuAccess() {
  const pathname = usePathname();
  const router = useRouter();
  const { isAuthenticated } = useAuthState();
  const routes = useSelector(selectAllowedRoutes);
  const menu = useSelector(selectMenuStatus);
  const dispatch = useDispatch<AppDispatch>();
  const exempt = routeAllowed(pathname, []);
  const ready = menu.permissionsLoaded && !menu.loading;
  const allowed = !isAuthenticated || exempt || (ready && routeAllowed(pathname, routes));
  useEffect(() => {
    if (isAuthenticated && !exempt && !ready && !menu.loading && !menu.error) void dispatch(fetchDynamicMenu());
  }, [isAuthenticated, exempt, ready, menu.loading, menu.error, dispatch]);
  useEffect(() => {
    if (isAuthenticated && ready && routes.length && !allowed) router.push('/unauthorized');
  }, [isAuthenticated, ready, routes.length, allowed, router]);
  return {
    allowed,
    loading: isAuthenticated && !exempt && !ready && !menu.error,
    error: isAuthenticated && menu.error,
    empty: isAuthenticated && ready && !routes.length,
    retry: () => { void dispatch(fetchDynamicMenu()); },
    signOut: () => { dispatch(logout()); window.location.href = '/login'; },
  };
}
